/**
 * The add-on modules a clinic can have — STORY-188a, MOD-D1 and MOD-D2.
 *
 * **The base plan is not in this list and never will be**: Home, Treatment
 * plans, Patients, Calendar, Staff with HR, Settings, basic stock and Google
 * sync are there for every clinic. Only what is sold separately is a module.
 *
 * A `const` array rather than an enum, like `ROLES`, because it is also the
 * list the operator console renders.
 */
export const ADD_ON_MODULES = ["SUPPLY", "ACCOUNTING", "CHATBOT"] as const;
export type ModuleName = (typeof ADD_ON_MODULES)[number];

export function isModuleName(value: string): value is ModuleName {
  return (ADD_ON_MODULES as readonly string[]).includes(value);
}

/** What each module is called on screen, and in the sentence a refusal carries. */
export const MODULE_LABELS: Record<ModuleName, string> = {
  SUPPLY: "Supply",
  ACCOUNTING: "Accounting",
  CHATBOT: "Chatbot",
};

/**
 * **Preview modules — switched on per clinic by an operator, and not sold**
 * (TK-D28, 2026-10-25). Off for every clinic, so a clinic that is live is not
 * touched until an operator chooses it. **Not an add-on**: no price, no place
 * in the bundle, no *Add it* prompt and no ask from the clinic. They share the
 * add-ons' table, guard and refusal; nothing about pricing knows they exist.
 */
export const PREVIEW_MODULES = ["TASKS", "WORKFLOWS"] as const;
export type PreviewModule = (typeof PREVIEW_MODULES)[number];

/** Anything a route can be gated on: an add-on, or a preview. */
export type GatedModule = ModuleName | PreviewModule;

export const PREVIEW_LABELS: Record<PreviewModule, string> = {
  TASKS: "Tasks",
  WORKFLOWS: "Workflows",
};

export function isPreviewModule(value: string): value is PreviewModule {
  return (PREVIEW_MODULES as readonly string[]).includes(value);
}

export function isGatedModule(value: string): value is GatedModule {
  return isModuleName(value) || isPreviewModule(value);
}

export function gatedModuleLabel(module: GatedModule): string {
  return isPreviewModule(module) ? PREVIEW_LABELS[module] : MODULE_LABELS[module];
}

/** One preview as the operator sees it for a clinic. */
export interface ClinicPreviewDto {
  module: PreviewModule;
  enabled: boolean;
  changedAt: string | null;
  changedByName: string | null;
}

/** One module as the operator sees it for a clinic. */
export interface ClinicModuleDto {
  module: ModuleName;
  enabled: boolean;
  /** When it was last switched, or null if it never has been (no row). */
  changedAt: string | null;
  /** The operator's name, or null for the migration or a removed operator. */
  changedByName: string | null;
  /** When the clinic's admin asked for it (STORY-188b), or null if they have not. */
  requestedAt: string | null;
}

/** One module as the clinic itself sees it (STORY-188b). */
export interface ClinicModuleStateDto {
  module: ModuleName;
  enabled: boolean;
  /** An admin has already asked for it, and an operator has not switched it on yet. */
  requested: boolean;
  /**
   * Switched off but its data kept, so it can still be read and not changed
   * (STORY-188c, MOD-D18). Always false for Chatbot, which has no such state.
   */
  readOnly: boolean;
}

/**
 * Modules whose data stays readable when they are switched off — **none, since
 * MS-D10 (2026-10-16)**, which reversed MOD-D18: an add-on switched off is
 * treated exactly as one never bought, its pages refused and its pieces hidden
 * inside other screens. **Nothing is deleted** (MOD-D12 stands): switching it on
 * again brings every record back. The read-only machinery stays, so restoring
 * MOD-D18 for a module is adding it back to this list.
 */
export const READ_ONLY_MODULES: readonly ModuleName[] = [];

/** The `code` on the 403 a locked module causes, so a screen can show the prompt (188b). */
export const MODULE_REQUIRED = "MODULE_REQUIRED";

/** The sentence a refusal carries. */
export function moduleRefusalMessage(module: GatedModule): string {
  return `This clinic does not have ${gatedModuleLabel(module)}. Ask your operator to add it.`;
}

/** The sentence for a change refused because the module is switched off but readable. */
export function moduleReadOnlyMessage(module: GatedModule): string {
  return `${gatedModuleLabel(module)} is switched off for this clinic, so it can be read but not changed. Ask your operator to switch it back on.`;
}
